import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaEye, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/fileManagerReport";

/* helpers */
const toStr = (v) => String(v ?? "").trim();

export default function FileManagerReport() {
  const navigate = useNavigate();
  const { isDark, skin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ common localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [loading, setLoading] = useState(false);

  const [meta, setMeta] = useState({
    branches: [],
    vehicleMainTypes: [],
  });

  const [filters, setFilters] = useState({
    branchId: "",
    vehCode: "",
    vehName: "",
    vehRegNo: "",
    vehicleTypeId: "Select",
  });

  const [rows, setRows] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [total, setTotal] = useState(0);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- load meta ---------------- */
  const loadMeta = async () => {
    try {
      const res = await axios.get(`${API_BASE}/meta`, {
        headers: apiHeaders,
      });

      setMeta({
        branches: res.data?.branches || [],
        vehicleMainTypes: res.data?.vehicleMainTypes || [],
      });

      // branch user = fixed branch
      if (branchType !== "HO") {
        setFilters((p) => ({ ...p, branchId: String(branchId) }));
      }
    } catch (e) {
      toast.error("Failed to load meta");
    }
  };

  /* ---------------- load list ---------------- */
  const loadList = async (override = null, pageOverride = null) => {
    const f = override || filters;
    const p = pageOverride ?? page;

    try {
      setLoading(true);

      // HO -> if empty => "Select"
      const branchIdToSend =
        branchType === "HO" ? (f.branchId || "Select") : String(branchId);

      const res = await axios.get(`${API_BASE}/list`, {
        headers: apiHeaders,
        params: {
          branchId: branchIdToSend,
          vehCode: f.vehCode || "",
          vehName: f.vehName || "",
          vehRegNo: f.vehRegNo || "",
          vehicleTypeId: f.vehicleTypeId || "Select",
          page: p,
          pageSize,
        },
      });

      setRows(res.data?.rows || []);
      setTotal(Number(res.data?.total || 0));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load list");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();

    const init = {
      branchId: branchType !== "HO" ? String(branchId) : "",
      vehCode: "",
      vehName: "",
      vehRegNo: "",
      vehicleTypeId: "Select",
    };

    setFilters(init);
    loadList(init, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    loadList(filters, 1);
  };

  const handleViewAll = () => {
    const reset = {
      branchId: branchType !== "HO" ? String(branchId) : "",
      vehCode: "",
      vehName: "",
      vehRegNo: "",
      vehicleTypeId: "Select",
    };
    setFilters(reset);
    setPage(1);
    loadList(reset, 1);
    toast.info("Showing all records");
  };

  const goToVehicleDetails = (vehicleId) => {
    if (!vehicleId) return;
    navigate(`/vehicle/OwnVehicle_DetailsEntry?vehicleId=${vehicleId}`);
  };

  /* ---------------- doc columns ---------------- */
  const docCols = useMemo(
    () => [
      { key: "INVOICE", label: "Invoice Copy" },
      { key: "RC", label: "RC Copy" },
      { key: "PERMIT", label: "Permit Copy" },
      { key: "INSURANCE", label: "Insurance Copy" },
      { key: "ROADTAX", label: "Road Tax Copy" },
      { key: "FITNESS", label: "Fitness Copy" },
      { key: "EMISSION", label: "Emission Copy" },
    ],
    []
  );

  const docIdFromRow = (r, docKey) => {
    const n = (x) => Number(x || 0);
    switch (docKey) {
      case "INVOICE":
        return n(r.fld_invoicedocumentid);
      case "RC":
        return n(r.fld_rccopydocumentid);
      case "PERMIT":
        return n(r.fld_permitdocumentid);
      case "INSURANCE":
        return n(r.fld_Insurancedocumentid);
      case "ROADTAX":
        return n(r.fld_roadtaxdocumentid);
      case "FITNESS":
        return n(r.fld_fitnessdocumentid);
      case "EMISSION":
        return n(r.fld_emissiondocumentid);
      default:
        return 0;
    }
  };

  const docEnabled = (r, docKey) => docIdFromRow(r, docKey) > 0;

  const navigateToDocPage = (row, docType) => {
    const map = {
      INVOICE: "/vehicle/FileManagerInvoiceDocs",
      RC: "/vehicle/FileManagerFCDocs",
      PERMIT: "/vehicle/FileManagerPermitDocs",
      INSURANCE: "/vehicle/FileManagerInsuranceDocs",
      ROADTAX: "/vehicle/FileManagerRoadTaxDoc",
      FITNESS: "/vehicle/FileManagerFitnessDoc",
      EMISSION: "/vehicle/FileManagerEmissionDoc",
    };

    const path = map[docType];
    if (!path) return;

    const vehicleId = Number(row.fld_vehicleid || 0);
    const docId = docIdFromRow(row, docType);

    if (!docId) return toast.info("No document");

    navigate(path, { state: { vehicleId, docId, docType } });
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const gotoPage = (p) => {
    const np = Math.min(totalPages, Math.max(1, p));
    setPage(np);
    loadList(filters, np);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">FILE MANAGER REPORT</span>}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              INFRA SCREEN : B-58
            </div>

            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
          </div>

          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Branch</label>
                <select
                  name="branchId"
                  value={filters.branchId}
                  onChange={handleChange}
                  className={inputClass}
                  disabled={branchType !== "HO"}
                >
                  <option value="">Select</option>
                  {meta.branches.map((b) => (
                    <option key={b.id} value={b.value}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Vehicle Reg.No</label>
                <input
                  name="vehRegNo"
                  value={filters.vehRegNo}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Type reg no"
                />
              </div>

              <div>
                <label className={labelClass}>Vehicle Code</label>
                <input
                  name="vehCode"
                  value={filters.vehCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Type code"
                />
              </div>

              <div>
                <label className={labelClass}>Vehicle Name</label>
                <input
                  name="vehName"
                  value={filters.vehName}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Type name"
                />
              </div>

              <div>
                <label className={labelClass}>Vehicle Main Type</label>
                <select
                  name="vehicleTypeId"
                  value={filters.vehicleTypeId}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="Select">Select</option>
                  {meta.vehicleMainTypes.map((t) => (
                    <option key={t.id} value={t.value}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleViewAll}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow"
              >
                <FaEye /> View All
              </button>

              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow`}
                disabled={loading}
              >
                <FaSearch /> {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-xl">
            <table className="min-w-[1200px] w-full text-sm border-collapse">
              <thead>
                <tr className={isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"}>
                  <th className="px-4 py-3 border" rowSpan={2}>S.No</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Vehicle Main Type</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Vehicle Code</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Vehicle Type</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Vehicle Name</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Vehicle Reg.No</th>
                  <th className="px-4 py-3 border" rowSpan={2}>Location</th>
                  <th className="px-4 py-3 border text-center" colSpan={7}>
                    Scanned Documents
                  </th>
                </tr>

                <tr className={isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"}>
                  {docCols.map((c) => (
                    <th
                      key={c.key}
                      className="px-3 py-2 border whitespace-nowrap text-center"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className={isDark ? "bg-gray-900" : "bg-white"}>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-center py-10">
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const vehicleId = Number(r.fld_vehicleid || 0);

                    return (
                      <tr
                        key={vehicleId || idx}
                        className={isDark ? "border-gray-700" : "border-gray-200"}
                      >
                        <td className="px-4 py-3 border text-center">
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className="px-4 py-3 border">{r.VehicleMainType || "-"}</td>

                        <td className="px-4 py-3 border text-center">
                          <button
                            className="text-blue-600 hover:underline font-semibold"
                            onClick={() => goToVehicleDetails(vehicleId)}
                            title="Open vehicle details"
                          >
                            {r.fld_vehicle_code || "-"}
                          </button>
                        </td>

                        <td className="px-4 py-3 border">{r.fld_vehicletypename || "-"}</td>
                        <td className="px-4 py-3 border">{r.fld_vehicle_name || "-"}</td>
                        <td className="px-4 py-3 border">{r.fld_vehicle_regno || "-"}</td>
                        <td className="px-4 py-3 border">{r.fld_branchname || "-"}</td>

                        {docCols.map((c) => {
                          const enabled = docEnabled(r, c.key);
                          return (
                            <td key={c.key} className="px-3 py-3 border text-center">
                              <button
                                disabled={!enabled}
                                onClick={() => navigateToDocPage(r, c.key)}
                                className={`font-semibold ${
                                  enabled
                                    ? "text-blue-600 hover:underline"
                                    : "text-gray-400 cursor-not-allowed"
                                }`}
                              >
                                View
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className={isDark ? "text-gray-300" : "text-gray-600"}>
              Page {page} of {totalPages}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => gotoPage(page - 1)}
                disabled={page <= 1}
                className={`px-4 py-2 border rounded ${
                  page <= 1
                    ? "text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p = i + 1;
                if (totalPages > 5) {
                  if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                }

                return (
                  <button
                    key={p}
                    onClick={() => gotoPage(p)}
                    className={`px-4 py-2 border rounded ${
                      p === page
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                onClick={() => gotoPage(page + 1)}
                disabled={page >= totalPages}
                className={`px-4 py-2 border rounded ${
                  page >= totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}